import { useState, useEffect, useRef } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { FileText, Eye, Edit3 } from 'lucide-react';

import { saveAs } from 'file-saver';
import { App } from 'antd';

interface MarkdownEditorProps {
  file: File;
  onClose: () => void;
}

export default function MarkdownEditor({ file }: MarkdownEditorProps) {
  const { message } = App.useApp();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');
  const previewRef = useRef<HTMLDivElement>(null);

  // 读取文件内容
  useEffect(() => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setContent(e.target?.result as string);
      setLoading(false);
    };
    reader.onerror = () => {
      message.error('文件读取失败');
      setLoading(false);
    };
    reader.readAsText(file);
  }, [file, message]);

  // 导出 Markdown 文件
  const exportMarkdown = () => {
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
    const fileName = file.name.replace(/\.md$/i, '_edited.md');
    saveAs(blob, fileName);
    message.success('Markdown 文件已下载');
  };

  // 导出 TXT 文件
  const exportText = () => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const fileName = file.name.replace(/\.md$/i, '_edited.txt');
    saveAs(blob, fileName);
    message.success('TXT 文件已下载');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        <span className="ml-3 text-gray-400">正在加载文件...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* 工具栏 */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-700 flex-shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab('edit')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all cursor-pointer ${
              activeTab === 'edit'
                ? 'bg-amber-600 text-white shadow-lg shadow-amber-500/30'
                : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800 hover:text-gray-200 border border-gray-700'
            }`}
          >
            <Edit3 className="w-4 h-4" />
            编辑
          </button>
          <button
            onClick={() => setActiveTab('preview')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all cursor-pointer ${
              activeTab === 'preview'
                ? 'bg-amber-600 text-white shadow-lg shadow-amber-500/30'
                : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800 hover:text-gray-200 border border-gray-700'
            }`}
          >
            <Eye className="w-4 h-4" />
            预览
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={exportMarkdown}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 rounded-lg text-white text-sm font-semibold shadow-lg shadow-amber-500/30 transition-all cursor-pointer"
          >
            <FileText className="w-4 h-4" />
            导出 MD
          </button>
          <button
            onClick={exportText}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 text-slate-300 rounded-xl hover:bg-slate-800 transition-all cursor-pointer border border-slate-700 hover:border-amber-500/50 hover:text-amber-400"
          >
            <FileText className="w-4 h-4" />
            导出 TXT
          </button>
        </div>
      </div>

      {/* 编辑/预览区域 */}
      <div className="flex-1 overflow-hidden bg-gray-900 rounded-xl border border-gray-700">
        {activeTab === 'edit' ? (
          <MDEditor
            value={content}
            onChange={(val) => setContent(val || '')}
            height="100%"
            className="bg-gray-900"
            preview="edit"
          />
        ) : (
          <div className="h-full overflow-auto p-8">
            <div
              ref={previewRef}
              style={{ backgroundColor: '#0d1117' }}
              className="prose prose-invert max-w-none bg-gray-800/50 p-8 rounded-xl border border-gray-700"
            >
              <MDEditor.Markdown source={content} />
            </div>
          </div>
        )}
      </div>

      {/* 底部信息 */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700 text-sm text-gray-500 flex-shrink-0">
        <span>字符数: {content.length}</span>
        <span>行数: {content.split('\n').length}</span>
      </div>

      {/* 动画样式 */}
      <style>{`
        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </div>
  );
}
